// campus-app/frontend/src/components/DemoCampusOverlay.tsx
/**
 * DemoCampusOverlay · Overlays y banners para usuarios en plan demo-start.
 *
 * Sprint feat/demo-start-access · 2026-07-01 · Carlos Beltrán (IA)
 *
 * Tres componentes:
 *  - DemoLockedContentOverlay: sobre contenido Start → redirige a ventas
 *  - DemoUpsellOverlay: sobre contenido Basic/Pro → modal "agenda llamada"
 *  - DemoExpiredBanner: banner fijo cuando demo_locked_at IS NOT NULL
 */
import { useState } from 'react';

// URL de ventas Robotea Start — configurable vía env para evitar hardcode.
const SALES_URL =
  import.meta.env.VITE_ROBOTEA_START_SALES_URL ?? 'https://start.robotea.es/especial';
// URL de agenda de llamada (Calendly o similar)
const CALL_URL =
  import.meta.env.VITE_ROBOTEA_CALL_URL ?? 'https://robotea.es/llamada';

// ── DemoLockedContentOverlay ─────────────────────────────────────────────────

interface DemoLockedContentOverlayProps {
  children: React.ReactNode;
  /** Si true, envuelve children con el overlay. Si false, renderiza children normal. */
  locked: boolean;
}

/**
 * Overlay transparente sobre contenido de Robotea Start para usuarios demo.
 * Al hacer clic → redirige a la URL de ventas (sin navegar dentro del campus).
 */
export function DemoLockedContentOverlay({
  children,
  locked,
}: DemoLockedContentOverlayProps): JSX.Element {
  if (!locked) return <>{children}</>;

  return (
    <div className="relative">
      {/* Contenido visible pero no interactuable */}
      <div className="pointer-events-none select-none opacity-40" aria-hidden="true">
        {children}
      </div>
      {/* Overlay clickable */}
      <button
        type="button"
        onClick={() => window.open(SALES_URL, '_blank', 'noopener,noreferrer')}
        className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg bg-gradient-to-b from-surface/60 to-surface/95 backdrop-blur-sm"
        data-testid="demo-locked-start-overlay"
        aria-label="Ver oferta de Robotea Start"
      >
        <span className="text-3xl" aria-hidden="true">🔒</span>
        <span className="text-center text-sm font-semibold text-ink">
          Disponible en Robotea Start
        </span>
        <span className="rounded-full bg-violet-600 px-4 py-1.5 text-xs font-bold text-white">
          Ver oferta →
        </span>
      </button>
    </div>
  );
}

// ── DemoUpsellOverlay ────────────────────────────────────────────────────────

interface DemoUpsellOverlayProps {
  children: React.ReactNode;
  locked: boolean;
}

/**
 * Overlay sobre contenido Basic/Pro para usuarios demo.
 * Al hacer clic → modal "¿Quieres que te expliquemos cómo funciona? Agenda una llamada."
 */
export function DemoUpsellOverlay({
  children,
  locked,
}: DemoUpsellOverlayProps): JSX.Element {
  const [open, setOpen] = useState(false);

  if (!locked) return <>{children}</>;

  return (
    <>
      <div className="relative">
        <div className="pointer-events-none select-none opacity-40" aria-hidden="true">
          {children}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg bg-gradient-to-b from-surface/60 to-surface/95 backdrop-blur-sm"
          data-testid="demo-locked-upsell-overlay"
          aria-label="Saber más sobre este plan"
        >
          <span className="text-3xl" aria-hidden="true">🔒</span>
          <span className="text-center text-sm font-semibold text-ink">
            Plan Basic / Pro
          </span>
          <span className="rounded-full border border-ink/20 bg-surface px-4 py-1.5 text-xs font-medium text-ink/80">
            ¿Quieres saber más?
          </span>
        </button>
      </div>

      {/* Modal "agenda llamada" */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="upsell-modal-title"
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
        >
          <div className="card w-full max-w-sm p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-violet-500/15 text-3xl">
              📞
            </div>
            <h2 id="upsell-modal-title" className="text-lg font-semibold text-ink">
              ¿Quieres que te expliquemos cómo funciona?
            </h2>
            <p className="mt-2 text-sm text-ink/70">
              Agenda una llamada gratuita con nuestro equipo y te contamos
              cómo los planes Basic y Pro pueden ayudarte a escalar.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <a
                href={CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full text-center"
                data-testid="upsell-modal-call-cta"
              >
                Agenda una llamada →
              </a>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-sm text-ink/50 hover:text-ink/80"
              >
                Ahora no
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── DemoExpiredBanner ────────────────────────────────────────────────────────

interface DemoExpiredBannerProps {
  /** demo_locked_at ISO string · si truthy, muestra el banner. */
  demoLockedAt: string | null | undefined;
}

/**
 * Banner persistente visible cuando el acceso demo ha vencido (demo_locked_at IS NOT NULL).
 * Bloquea la navegación al campus mostrando CTA a comprar Robotea Start.
 */
export function DemoExpiredBanner({ demoLockedAt }: DemoExpiredBannerProps): JSX.Element | null {
  if (!demoLockedAt) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-canvas/90 backdrop-blur-sm"
      role="alert"
      aria-live="assertive"
      data-testid="demo-expired-banner"
    >
      <div className="card w-full max-w-md p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/15 text-3xl">
          ⏰
        </div>
        <h2 className="text-xl font-semibold text-ink">Tu acceso demo ha vencido</h2>
        <p className="mt-3 text-sm text-ink/70">
          Los 30 días de prueba han terminado. Para seguir accediendo al campus
          y activar tu robot en cuenta real, hazte con Robotea Start completo.
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <a
            href={SALES_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary w-full text-center"
            data-testid="demo-expired-buy-cta"
          >
            Quiero Robotea Start →
          </a>
        </div>
        <p className="mt-4 text-xs text-ink/40">
          Tu cuenta se elimina automáticamente 30 días después de la expiración.
        </p>
      </div>
    </div>
  );
}
